// soft-delete-addon.ts -----------------------------------------------
import Dexie from 'dexie'
import type { Table, Collection } from 'dexie'

export interface SoftDeleteConfig {
  updatedField?: string
  deletedField?: string
  enabledTables?: string[] // If undefined, enable for all tables
}

export interface SoftDeleteTableConfig {
  updatedField: string
  deletedField: string
  primaryKey: string
}

// Store original methods for hard delete access
/**
 * Each database's own delete methods, from before the addon replaced them:
 * per database, and taken once, so a second install on the same database
 * (React StrictMode runs the SyncManager's useMemo twice in development) can't
 * take the soft-delete wrappers for the originals and turn `hardDelete` soft
 * (K24).
 */
const originalMethods = new WeakMap<
  Dexie,
  { tableDelete: any; collectionDelete: any; bulkDelete: any }
>()

/**
 * Dexie addon that implements soft delete by overriding delete methods.
 * Instead of physically deleting records, it updates them with deletion timestamps.
 */
export function SoftDeleteAddon(
  db: Dexie,
  config: SoftDeleteConfig = {}
): void {
  // Installed already: its delete methods are the wrappers
  if (originalMethods.has(db)) return

  const {
    updatedField = 'updated_at',
    deletedField = 'deleted_at',
    enabledTables,
  } = config

  // Store table configurations
  const tableConfigs = new Map<string, SoftDeleteTableConfig>()

  // Initialize table configurations
  for (const table of db.tables) {
    const tableName = table.name
    
    // Skip if not enabled for this table
    if (enabledTables && !enabledTables.includes(tableName)) {
      continue
    }

    const primaryKey = table.schema.primKey.name
    tableConfigs.set(tableName, {
      updatedField,
      deletedField,
      primaryKey,
    })
  }

  // Helper function to perform soft delete on a single record
  async function softDeleteRecord(
    table: Table,
    key: any,
    tableConfig: SoftDeleteTableConfig
  ): Promise<void> {
    const { updatedField, deletedField, primaryKey } = tableConfig
    const now = new Date()

    // Try to get existing record
    const existingRecord = await table.get(key)
    
    if (existingRecord) {
      // Update existing record with deletion timestamps
      const updates: any = {
        [updatedField]: now,
        [deletedField]: now,
      }
      await table.update(key, updates)
    } else {
      // Create tombstone record for non-existent record
      const tombstone: any = {
        [primaryKey]: key,
        [updatedField]: now,
        [deletedField]: now,
      }
      await table.put(tombstone)
    }
  }

  // Store original methods before overriding
  originalMethods.set(db, {
    tableDelete: db.Table.prototype.delete,
    collectionDelete: db.Collection.prototype.delete,
    bulkDelete: db.Table.prototype.bulkDelete,
  })

  // Override Table.prototype.delete
  db.Table.prototype.delete = Dexie.override(
    db.Table.prototype.delete,
    function (originalDelete) {
      return async function (this: Table, key: any): Promise<void> {
        const tableConfig = tableConfigs.get(this.name)
        
        if (!tableConfig) {
          // Not configured for soft delete, use original method
          return originalDelete.call(this, key)
        }

        await softDeleteRecord(this, key, tableConfig)
      }
    }
  )

  // Override Collection.prototype.delete
  db.Collection.prototype.delete = Dexie.override(
    db.Collection.prototype.delete,
    function (originalDelete) {
      return async function (this: Collection): Promise<number> {
        // Access table through the db property - Collection doesn't have direct table reference
        const tableName = (this as any)._ctx.table.name
        const tableConfig = tableConfigs.get(tableName)
        
        if (!tableConfig) {
          // Not configured for soft delete, use original method
          return originalDelete.call(this)
        }

        // Get all keys from the collection
        const keys = await this.primaryKeys()
        
        // Get table reference from db
        const table = db.table(tableName)
        
        // Perform soft delete on each record
        await Promise.all(
          keys.map((key) => 
            softDeleteRecord(table, key, tableConfig)
          )
        )

        return keys.length
      }
    }
  )

  // Override Table.prototype.bulkDelete
  db.Table.prototype.bulkDelete = Dexie.override(
    db.Table.prototype.bulkDelete,
    function (originalBulkDelete) {
      return async function (this: Table, keys: any[]): Promise<void> {
        const tableConfig = tableConfigs.get(this.name)
        
        if (!tableConfig) {
          // Not configured for soft delete, use original method
          return originalBulkDelete.call(this, keys)
        }

        // Perform soft delete on each key
        await Promise.all(
          keys.map((key) => 
            softDeleteRecord(this, key, tableConfig)
          )
        )
      }
    }
  )
}

/**
 * Performs a hard delete using the original delete method, bypassing soft delete.
 * Useful for server sync operations that need to physically remove records.
 */
export function hardDelete(table: Table, key: any): Promise<void> {
  const originals = originalMethods.get(table.db)
  if (!originals) {
    throw new Error('SoftDeleteAddon must be installed before using hardDelete')
  }
  return originals.tableDelete.call(table, key)
}

/**
 * Performs a hard bulk delete using the original method, bypassing soft delete.
 */
export function hardBulkDelete(table: Table, keys: any[]): Promise<void> {
  const originals = originalMethods.get(table.db)
  if (!originals) {
    throw new Error('SoftDeleteAddon must be installed before using hardBulkDelete')
  }
  return originals.bulkDelete.call(table, keys)
}

// Register the addon to be included by default (optional)
// Comment this out if you prefer explicit registration
// Dexie.addons.push(SoftDeleteAddon) 